import { useState, useEffect, useMemo } from "react";
import { TrendingUp, Shield, Diamond, Landmark, Rocket, RefreshCw, ShoppingCart, X, AlertTriangle } from "lucide-react";
import {
  fetchStockPicks, buyStock, triggerStockPicks, parseUTC,
  type StockPick, type StockPicksResult, type StockPickCategory,
} from "../api";
import { useAuth } from "../AuthContext";

function timeAgo(isoString: string): string {
  const diff = Date.now() - parseUTC(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<StockPickCategory, {
  label: string;
  icon: typeof TrendingUp;
  color: string;
  description: string;
}> = {
  growth: {
    label: "Growth",
    icon: TrendingUp,
    color: "#38bdf8",
    description: "High-growth stocks with strong momentum. Higher risk, higher potential returns.",
  },
  long_term: {
    label: "Long Term",
    icon: Shield,
    color: "#818cf8",
    description: "Blue-chip stocks built for 5+ year holding. Stable, proven market leaders.",
  },
  value: {
    label: "Value",
    icon: Diamond,
    color: "#2dd4bf",
    description: "Undervalued stocks trading below their intrinsic worth. Buy low, hold strong.",
  },
  super_long_term: {
    label: "Super Long Term",
    icon: Landmark,
    color: "#c084fc",
    description: "Generational wealth builders. Ultra-stable compounders for 10+ years.",
  },
  moonshot: {
    label: "Moonshot",
    icon: Rocket,
    color: "#f472b6",
    description: "Under-the-radar stocks with explosive potential. High revenue growth, early-stage companies that could 10x.",
  },
};

const CATEGORIES: StockPickCategory[] = ["growth", "value", "long_term", "super_long_term", "moonshot"];

// ─── Subcomponents ────────────────────────────────────────────────────────────

function ScoreBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <span style={{ width: 85, fontSize: 10, color: "#475569", fontFamily: "monospace" }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, value)}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.5s ease" }} />
      </div>
      <span style={{ width: 28, fontSize: 11, color: "#94a3b8", fontFamily: "'Space Mono', monospace", textAlign: "right" }}>
        {value.toFixed(0)}
      </span>
    </div>
  );
}

function FundStat({ label, value, good, bad }: { label: string; value: string; good: boolean; bad: boolean }) {
  const color = good ? "#4ade80" : bad ? "#f87171" : "#94a3b8";
  return (
    <span style={{ fontSize: 11, fontFamily: "'Space Mono', monospace" }}>
      <span style={{ color: "#64748b" }}>{label} </span>
      <span style={{ color, fontWeight: 600 }}>{value}</span>
    </span>
  );
}

function SignalBadge({ signal }: { signal: string }) {
  const config: Record<string, { color: string; bg: string; label: string }> = {
    strong_buy: { color: "#4ade80", bg: "#4ade8015", label: "STRONG BUY" },
    buy: { color: "#60a5fa", bg: "#60a5fa15", label: "BUY" },
    hold: { color: "#facc15", bg: "#facc1515", label: "HOLD" },
    sell: { color: "#fb923c", bg: "#fb923c15", label: "SELL" },
    strong_sell: { color: "#f87171", bg: "#f8717115", label: "STRONG SELL" },
  };
  const c = config[signal] ?? config.hold;
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 12, background: c.bg, color: c.color,
      fontSize: 10, fontWeight: 700, fontFamily: "monospace", letterSpacing: 1, border: `1px solid ${c.color}30`,
    }}>
      {c.label}
    </span>
  );
}

function formatMarketCap(mc: number): string {
  if (mc >= 1e12) return `$${(mc / 1e12).toFixed(1)}T`;
  if (mc >= 1e9) return `$${(mc / 1e9).toFixed(1)}B`;
  if (mc >= 1e6) return `$${(mc / 1e6).toFixed(0)}M`;
  return `$${mc.toFixed(0)}`;
}

function pctColor(v: number | null): string {
  if (v == null) return "#475569";
  return v >= 0 ? "#4ade80" : "#f87171";
}

function pctStr(v: number | null): string {
  if (v == null) return "--";
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

// ─── Buy Modal ────────────────────────────────────────────────────────────────

function BuyModal({ pick, category, onClose, onBuy }: {
  pick: StockPick;
  category: StockPickCategory;
  onClose: () => void;
  onBuy: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const totalCost = pick.price * quantity;

  async function handleBuy() {
    setSubmitting(true);
    try {
      await buyStock({
        pick_id: pick.id,
        ticker: pick.ticker,
        category,
        buy_price: pick.price,
        quantity,
        notes: notes || undefined,
        is_agent_pick: true,
      });
      onBuy();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16, padding: 28, width: 420,
        animation: "fadeIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>BUY STOCK</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginTop: 4 }}>
              {pick.ticker} <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 400 }}>@ ${pick.price.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{
            padding: "6px 12px", borderRadius: 8, background: `${CATEGORY_CONFIG[category].color}15`,
            color: CATEGORY_CONFIG[category].color, fontSize: 11, fontWeight: 600, display: "inline-block",
            fontFamily: "monospace",
          }}>
            {CATEGORY_CONFIG[category].label}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>SHARES</label>
          <input
            type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
            }}
          />
        </div>

        <div style={{
          background: "#050c18", border: "1px solid #1e293b", borderRadius: 8, padding: 14, marginBottom: 16,
          display: "flex", justifyContent: "space-between",
        }}>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>TOTAL COST</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
            ${totalCost.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>NOTES (OPTIONAL)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Long-term core position"
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#94a3b8", fontSize: 13,
            }}
          />
        </div>

        <button onClick={handleBuy} disabled={submitting} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
          color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <ShoppingCart size={16} />
          {submitting ? "RECORDING..." : "RECORD PURCHASE"}
        </button>
      </div>
    </div>
  );
}

// ─── Stock Card ───────────────────────────────────────────────────────────────

function StockCard({ pick, categoryColor, onBuy }: {
  pick: StockPick;
  categoryColor: string;
  onBuy: () => void;
}) {
  return (
    <div style={{
      background: "#0a1628", border: "1px solid #1e293b", borderRadius: 14,
      padding: 20, animation: "fadeIn 0.3s ease", borderTop: `3px solid ${categoryColor}`,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, color: categoryColor, fontFamily: "monospace", fontWeight: 700 }}>#{pick.rank}</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>{pick.ticker}</span>
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{pick.name}</div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>
            {pick.sector} {pick.market_cap > 0 ? `· ${formatMarketCap(pick.market_cap)}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
            ${pick.price.toFixed(2)}
          </div>
          <SignalBadge signal={pick.signal} />
        </div>
      </div>

      {/* ETF Tier Badge + Fund Age (for discovered ETFs) */}
      {pick.etf_tier === 'discovered' && (
        <div style={{
          display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap",
        }}>
          {pick.fund_age_years != null && pick.fund_age_years > 0 && (
            <span style={{
              fontSize: 10, fontFamily: "monospace", fontWeight: 600,
              padding: "3px 8px", borderRadius: 6,
              background: pick.fund_age_years >= 5 ? "#16a34a18" : pick.fund_age_years >= 3 ? "#d9770618" : "#f8717118",
              color: pick.fund_age_years >= 5 ? "#4ade80" : pick.fund_age_years >= 3 ? "#fb923c" : "#f87171",
              border: `1px solid ${pick.fund_age_years >= 5 ? "#4ade8030" : pick.fund_age_years >= 3 ? "#fb923c30" : "#f8717130"}`,
            }}>
              {pick.fund_age_years >= 10 ? `Est. ${Math.round(pick.fund_age_years)}yr` : `${pick.fund_age_years.toFixed(1)}yr old`}
            </span>
          )}
          {pick.market_cap > 0 && (
            <span style={{
              fontSize: 10, fontFamily: "monospace", fontWeight: 600,
              padding: "3px 8px", borderRadius: 6,
              background: "#1e293b", color: "#94a3b8",
            }}>
              {formatMarketCap(pick.market_cap)} AUM
            </span>
          )}
        </div>
      )}

      {/* Returns */}
      <div style={{
        display: "grid",
        gridTemplateColumns: (pick.return_3y != null || pick.return_5y != null) ? "1fr 1fr 1fr 1fr 1fr 1fr" : "1fr 1fr 1fr 1fr",
        gap: 8, marginBottom: 14,
        background: "#050c18", borderRadius: 8, padding: "8px 10px",
      }}>
        {[
          { label: "1M", value: pick.change_1m },
          { label: "3M", value: pick.change_3m },
          { label: "6M", value: pick.change_6m },
          { label: "1Y", value: pick.change_1y },
          ...(pick.return_3y != null ? [{ label: "3Y", value: pick.return_3y }] : []),
          ...(pick.return_5y != null ? [{ label: "5Y", value: pick.return_5y }] : []),
        ].map(r => (
          <div key={r.label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#475569", fontFamily: "monospace" }}>{r.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(r.value), fontFamily: "'Space Mono', monospace" }}>
              {pctStr(r.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Scores */}
      <div style={{ marginBottom: 14 }}>
        <ScoreBar label="Momentum" value={pick.score_momentum} color="#38bdf8" />
        <ScoreBar label="Volatility" value={pick.score_volatility} color="#818cf8" />
        <ScoreBar label="Fundamental" value={pick.score_fundamental} color="#c084fc" />
        <ScoreBar label="Technical" value={pick.score_technical} color="#facc15" />
        <ScoreBar label="Sentiment" value={pick.score_sentiment} color="#fb923c" />
        <ScoreBar label="Value" value={pick.score_value} color="#2dd4bf" />
      </div>

      {/* Fundamentals Row */}
      {pick.asset_type !== 'etf' && (pick.pe_ratio != null || pick.roe != null || pick.debt_equity != null || pick.revenue_growth != null) && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "6px 12px",
          background: "#050c18", borderRadius: 8, padding: "8px 12px", marginBottom: 10,
        }}>
          <span style={{ fontSize: 9, color: "#475569", fontFamily: "monospace", letterSpacing: 0.5, width: "100%", marginBottom: 2 }}>
            {pick.category === 'moonshot' ? 'GROWTH METRICS' : 'FUNDAMENTALS'}
          </span>
          {pick.category === 'moonshot' ? (
            <>
              {pick.revenue_growth != null && <FundStat label="Rev↑" value={`${pick.revenue_growth.toFixed(0)}%`} good={pick.revenue_growth >= 20} bad={pick.revenue_growth < 5} />}
              {pick.eps_growth != null && <FundStat label="EPS↑" value={`${pick.eps_growth.toFixed(0)}%`} good={pick.eps_growth >= 0} bad={pick.eps_growth < -30} />}
              {pick.net_margin != null && <FundStat label="Margin" value={`${pick.net_margin.toFixed(1)}%`} good={pick.net_margin >= 0} bad={pick.net_margin < -20} />}
              {pick.free_cash_flow_ps != null && <FundStat label="FCF/sh" value={`$${pick.free_cash_flow_ps.toFixed(1)}`} good={pick.free_cash_flow_ps > 0} bad={pick.free_cash_flow_ps < -5} />}
              {pick.debt_equity != null && <FundStat label="D/E" value={pick.debt_equity.toFixed(2)} good={pick.debt_equity < 1} bad={pick.debt_equity > 3} />}
              {pick.pe_ratio != null && pick.pe_ratio > 0 && <FundStat label="P/E" value={pick.pe_ratio.toFixed(1)} good={false} bad={false} />}
            </>
          ) : (
            <>
              {pick.pe_ratio != null && <FundStat label="P/E" value={pick.pe_ratio.toFixed(1)} good={pick.pe_ratio < 25} bad={pick.pe_ratio > 40} />}
              {pick.roe != null && <FundStat label="ROE" value={`${pick.roe.toFixed(1)}%`} good={pick.roe >= 15} bad={pick.roe < 5} />}
              {pick.debt_equity != null && <FundStat label="D/E" value={pick.debt_equity.toFixed(2)} good={pick.debt_equity < 0.5} bad={pick.debt_equity > 2} />}
              {pick.net_margin != null && <FundStat label="Margin" value={`${pick.net_margin.toFixed(1)}%`} good={pick.net_margin >= 15} bad={pick.net_margin < 0} />}
              {pick.eps_growth != null && <FundStat label="EPS↑" value={`${pick.eps_growth.toFixed(0)}%`} good={pick.eps_growth >= 10} bad={pick.eps_growth < 0} />}
              {pick.revenue_growth != null && <FundStat label="Rev↑" value={`${pick.revenue_growth.toFixed(0)}%`} good={pick.revenue_growth >= 10} bad={pick.revenue_growth < 0} />}
              {pick.free_cash_flow_ps != null && <FundStat label="FCF/sh" value={`$${pick.free_cash_flow_ps.toFixed(1)}`} good={pick.free_cash_flow_ps > 3} bad={pick.free_cash_flow_ps < 0} />}
              {pick.dividend_yield != null && pick.dividend_yield > 0 && <FundStat label="Div" value={`${pick.dividend_yield.toFixed(1)}%`} good={pick.dividend_yield >= 2} bad={false} />}
            </>
          )}
        </div>
      )}

      {/* Total Score */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: "#050c18", borderRadius: 8, padding: "8px 12px", marginBottom: 14,
      }}>
        <span style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>TOTAL SCORE</span>
        <span style={{
          fontSize: 22, fontWeight: 800, fontFamily: "'Space Mono', monospace",
          color: pick.score_total >= 70 ? "#4ade80" : pick.score_total >= 50 ? "#60a5fa" : "#facc15",
        }}>
          {pick.score_total.toFixed(1)}
        </span>
      </div>

      {/* Target Prices */}
      {(pick.target_price_agent || pick.target_price_market) && (
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14,
          background: "#050c18", borderRadius: 8, padding: "10px 12px",
        }}>
          {pick.target_price_agent != null && (
            <div>
              <div style={{ fontSize: 9, color: "#475569", fontFamily: "monospace", letterSpacing: 0.5, marginBottom: 3 }}>AGENT TARGET</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
                  ${pick.target_price_agent.toFixed(2)}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, fontFamily: "'Space Mono', monospace",
                  color: (pick.target_upside_agent ?? 0) >= 0 ? "#4ade80" : "#f87171",
                }}>
                  {(pick.target_upside_agent ?? 0) >= 0 ? "+" : ""}{(pick.target_upside_agent ?? 0).toFixed(1)}%
                </span>
              </div>
            </div>
          )}
          {pick.target_price_market != null && (
            <div>
              <div style={{ fontSize: 9, color: "#475569", fontFamily: "monospace", letterSpacing: 0.5, marginBottom: 3 }}>MARKET TARGET</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
                  ${pick.target_price_market.toFixed(2)}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, fontFamily: "'Space Mono', monospace",
                  color: (pick.target_upside_market ?? 0) >= 0 ? "#4ade80" : "#f87171",
                }}>
                  {(pick.target_upside_market ?? 0) >= 0 ? "+" : ""}{(pick.target_upside_market ?? 0).toFixed(1)}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 14,
        fontSize: 10, fontFamily: "monospace",
      }}>
        <div><span style={{ color: "#475569" }}>SMA50</span> <span style={{ color: pctColor(pick.sma50_pct) }}>{pctStr(pick.sma50_pct)}</span></div>
        <div><span style={{ color: "#475569" }}>SMA200</span> <span style={{ color: pctColor(pick.sma200_pct) }}>{pctStr(pick.sma200_pct)}</span></div>
        <div><span style={{ color: "#475569" }}>HV30</span> <span style={{ color: "#94a3b8" }}>{pick.hv30?.toFixed(1) ?? "--"}%</span></div>
        <div><span style={{ color: "#475569" }}>IV Rank</span> <span style={{ color: "#94a3b8" }}>{pick.iv_rank?.toFixed(0) ?? "--"}</span></div>
        <div><span style={{ color: "#475569" }}>IV/HV</span> <span style={{ color: "#94a3b8" }}>{pick.iv_hv_ratio?.toFixed(2) ?? "--"}x</span></div>
        <div><span style={{ color: "#475569" }}>Beta</span> <span style={{ color: "#94a3b8" }}>{pick.beta_proxy?.toFixed(2) ?? "--"}</span></div>
      </div>

      {/* Signal reason */}
      {pick.signal_reason && (
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 14, lineHeight: 1.5, fontStyle: "italic" }}>
          {pick.signal_reason}
        </div>
      )}

      {/* Buy button */}
      <button onClick={onBuy} style={{
        width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid #1d4ed830",
        background: "linear-gradient(135deg, #1d4ed815 0%, #3b82f615 100%)",
        color: "#60a5fa", fontSize: 13, fontWeight: 700, cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        transition: "all 0.15s ease",
      }}>
        <ShoppingCart size={14} />
        BUY THIS STOCK
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type ViewTab = 'stocks' | 'etfs';
const VALID_VIEW_TABS: ViewTab[] = ['stocks', 'etfs'];
const VALID_CATEGORIES: StockPickCategory[] = ['growth', 'value', 'long_term', 'super_long_term', 'moonshot'];

function parseStockPicksSub(subTab: string | null): { view: ViewTab; cat: StockPickCategory } {
  if (!subTab) return { view: 'stocks', cat: 'growth' };
  const parts = subTab.split('/');
  const view = VALID_VIEW_TABS.includes(parts[0] as ViewTab) ? (parts[0] as ViewTab) : 'stocks';
  const cat = VALID_CATEGORIES.includes(parts[1] as StockPickCategory) ? (parts[1] as StockPickCategory) : 'growth';
  return { view, cat };
}

export default function StockPicksPage({ subTab, onSubTabChange }: {
  subTab: string | null;
  onSubTabChange: (s: string | null) => void;
}) {
  const { isAdmin } = useAuth();
  const parsed = parseStockPicksSub(subTab);
  const [result, setResult] = useState<StockPicksResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const viewTab = parsed.view;
  const category = parsed.cat;

  function setViewTab(v: ViewTab) {
    onSubTabChange(`${v}/${category}`);
  }
  function setCategory(c: StockPickCategory) {
    onSubTabChange(`${viewTab}/${c}`);
  }
  const [buyModal, setBuyModal] = useState<StockPick | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const picks = await fetchStockPicks();
      setResult(picks);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleTrigger() {
    setRefreshing(true);
    try {
      await triggerStockPicks();
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Trigger failed");
    } finally {
      setRefreshing(false);
    }
  }

  const allPicks = useMemo(() => {
    if (!result) return [];
    return result[category] ?? [];
  }, [result, category]);

  const etfPicks = useMemo(() => allPicks.filter(p => p.asset_type === 'etf'), [allPicks]);
  const curatedEtfPicks = useMemo(() => etfPicks.filter(p => p.etf_tier === 'curated' || !p.etf_tier), [etfPicks]);
  const discoveredEtfPicks = useMemo(() => etfPicks.filter(p => p.etf_tier === 'discovered'), [etfPicks]);
  const stockPicks = useMemo(() => allPicks.filter(p => p.asset_type !== 'etf'), [allPicks]);
  const [etfSubTab, setEtfSubTab] = useState<'curated' | 'discovered'>('curated');

  const stats = useMemo(() => {
    if (allPicks.length === 0) return { avgScore: 0, topMomentum: "--", avgIVRank: 0, etfCount: 0, stockCount: 0 };
    const avgScore = allPicks.reduce((s, p) => s + p.score_total, 0) / allPicks.length;
    const topMom = allPicks.reduce((best, p) => p.score_momentum > best.score_momentum ? p : best, allPicks[0]);
    const avgIVRank = allPicks.reduce((s, p) => s + (p.iv_rank ?? 0), 0) / allPicks.length;
    return { avgScore, topMomentum: topMom.ticker, avgIVRank, etfCount: etfPicks.length, stockCount: stockPicks.length };
  }, [allPicks, etfPicks, stockPicks]);

  const catConfig = CATEGORY_CONFIG[category];
  const CatIcon = catConfig.icon;

  return (
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "32px 40px 60px" }}>

      {/* ── Category Tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {CATEGORIES.map(cat => {
          const cfg = CATEGORY_CONFIG[cat];
          const Icon = cfg.icon;
          const active = category === cat;
          return (
            <button key={cat} onClick={() => setCategory(cat)} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 20px", borderRadius: 12,
              border: `1px solid ${active ? cfg.color + "50" : "#1e293b"}`,
              background: active ? cfg.color + "12" : "#0a1628",
              color: active ? cfg.color : "#475569",
              fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s ease",
            }}>
              <Icon size={16} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* ── Description Banner ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        background: catConfig.color + "08", border: `1px solid ${catConfig.color}20`,
        borderRadius: 10, padding: "12px 18px", marginBottom: 24,
      }}>
        <CatIcon size={20} color={catConfig.color} />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>{catConfig.description}</span>
      </div>

      {/* ── View Switcher (Stocks / ETFs) ── */}
      {!loading && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 20, gap: 12,
        }}>
          <div style={{
            display: "inline-flex", background: "#0a1628", border: "1px solid #1e293b",
            borderRadius: 10, padding: 3, gap: 2,
          }}>
            {([
              { key: 'stocks' as ViewTab, label: 'Stocks', count: stockPicks.length, color: catConfig.color },
              { key: 'etfs' as ViewTab, label: 'ETFs', count: etfPicks.length, color: '#a78bfa' },
            ]).map(tab => {
              const active = viewTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setViewTab(tab.key)} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 8, border: "none",
                  background: active ? tab.color + "18" : "transparent",
                  color: active ? tab.color : "#475569",
                  fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s ease",
                }}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{
                      fontSize: 10, fontFamily: "'Space Mono', monospace",
                      color: active ? tab.color : "#334155", fontWeight: 600,
                    }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {result?.run_at && (
              <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace" }}>
                Analyzed {timeAgo(result.run_at)}
                {" · "}
                <span style={{ color: "#64748b" }}>
                  {parseUTC(result.run_at).toLocaleString(undefined, {
                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
                  })}
                </span>
              </span>
            )}
            {isAdmin && (
            <button onClick={handleTrigger} disabled={refreshing} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: 8, border: "1px solid #1e293b",
              background: "#0d1526", color: "#64748b", cursor: refreshing ? "default" : "pointer",
              fontSize: 12, fontFamily: "monospace",
            }}>
              <RefreshCw size={13} style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }} />
              {refreshing ? "SCREENING..." : "REFRESH"}
            </button>
            )}
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{
          background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
          padding: 16, marginBottom: 20, display: "flex", gap: 10, alignItems: "center",
        }}>
          <AlertTriangle size={16} color="#f87171" />
          <span style={{ color: "#f87171", fontSize: 13 }}>{error}</span>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              height: 420, background: "#0a1628", borderRadius: 14,
              border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite",
              opacity: 1 - i * 0.1,
            }} />
          ))}
        </div>
      )}

      {/* ── Stocks View ── */}
      {!loading && viewTab === 'stocks' && (
        <>
          {/* Stats Bar */}
          {allPicks.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              {[
                { label: "AVG SCORE", value: stats.avgScore.toFixed(1), color: catConfig.color },
                { label: "TOP MOMENTUM", value: stats.topMomentum, color: "#38bdf8" },
                { label: "AVG IV RANK", value: stats.avgIVRank.toFixed(0), color: "#facc15" },
              ].map(s => (
                <div key={s.label} style={{
                  background: "#0a1628", border: "1px solid #1e293b", borderRadius: 10, padding: "12px 16px",
                }}>
                  <div style={{ fontSize: 9, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontFamily: "'Space Mono', monospace" }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {stockPicks.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
              {stockPicks.map(pick => (
                <StockCard
                  key={pick.ticker}
                  pick={pick}
                  categoryColor={catConfig.color}
                  onBuy={() => setBuyModal(pick)}
                />
              ))}
            </div>
          ) : !error && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#334155" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>&#x25C8;</div>
              <div style={{ fontFamily: "monospace", marginBottom: 16 }}>No stock picks yet</div>
              {isAdmin && (
              <button onClick={handleTrigger} disabled={refreshing} style={{
                padding: "10px 24px", borderRadius: 10, border: "1px solid #1d4ed850",
                background: "#1d4ed815", color: "#60a5fa", fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                Run Stock Scan
              </button>
              )}
            </div>
          )}
        </>
      )}

      {/* ── ETFs View ── */}
      {!loading && viewTab === 'etfs' && (
        <>
          {/* ETF Sub-tabs: Curated / Discovered */}
          <div style={{
            display: "inline-flex", background: "#0a1628", border: "1px solid #1e293b",
            borderRadius: 10, padding: 3, gap: 2, marginBottom: 20,
          }}>
            {([
              { key: 'curated' as const, label: 'Curated', count: curatedEtfPicks.length },
              { key: 'discovered' as const, label: 'Discovered', count: discoveredEtfPicks.length },
            ]).map(tab => {
              const active = etfSubTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setEtfSubTab(tab.key)} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "7px 14px", borderRadius: 8, border: "none",
                  background: active ? "#a78bfa18" : "transparent",
                  color: active ? "#a78bfa" : "#475569",
                  fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s ease",
                }}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{
                      fontSize: 10, fontFamily: "'Space Mono', monospace",
                      color: active ? "#a78bfa" : "#334155", fontWeight: 600,
                    }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sub-tab description */}
          <div style={{
            fontSize: 12, color: "#64748b", marginBottom: 16, fontFamily: "monospace",
          }}>
            {etfSubTab === 'curated'
              ? 'Trusted, liquid, well-known ETFs — index, sector, commodity, bond, and dividend funds.'
              : 'Dynamically discovered ETFs filtered by AUM, volume, and track record. Updated weekly.'}
          </div>

          {/* Discovered ETF stats bar */}
          {etfSubTab === 'discovered' && discoveredEtfPicks.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              {(() => {
                const withAge = discoveredEtfPicks.filter(p => p.fund_age_years != null && p.fund_age_years > 0);
                const avgAge = withAge.length > 0 ? withAge.reduce((s, p) => s + (p.fund_age_years ?? 0), 0) / withAge.length : 0;
                const with5y = discoveredEtfPicks.filter(p => p.return_5y != null);
                const avg5y = with5y.length > 0 ? with5y.reduce((s, p) => s + (p.return_5y ?? 0), 0) / with5y.length : 0;
                const avgScore = discoveredEtfPicks.reduce((s, p) => s + p.score_total, 0) / discoveredEtfPicks.length;
                return [
                  { label: "AVG FUND AGE", value: `${avgAge.toFixed(1)}yr`, color: "#a78bfa" },
                  { label: "AVG 5Y RETURN", value: with5y.length > 0 ? `${avg5y >= 0 ? "+" : ""}${avg5y.toFixed(1)}%` : "--", color: avg5y >= 0 ? "#4ade80" : "#f87171" },
                  { label: "AVG SCORE", value: avgScore.toFixed(1), color: "#38bdf8" },
                ];
              })().map(s => (
                <div key={s.label} style={{
                  background: "#0a1628", border: "1px solid #1e293b", borderRadius: 10, padding: "12px 16px",
                }}>
                  <div style={{ fontSize: 9, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontFamily: "'Space Mono', monospace" }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* ETF cards */}
          {(() => {
            const activeETFs = etfSubTab === 'curated' ? curatedEtfPicks : discoveredEtfPicks;
            return activeETFs.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
                {activeETFs.map(pick => (
                  <StockCard
                    key={pick.ticker}
                    pick={pick}
                    categoryColor="#a78bfa"
                    onBuy={() => setBuyModal(pick)}
                  />
                ))}
              </div>
            ) : !error && (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#334155" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>&#x25C8;</div>
                <div style={{ fontFamily: "monospace", marginBottom: 16 }}>
                  No {etfSubTab} ETF picks for {catConfig.label}
                </div>
                {isAdmin && (
                <button onClick={handleTrigger} disabled={refreshing} style={{
                  padding: "10px 24px", borderRadius: 10, border: "1px solid #1d4ed850",
                  background: "#1d4ed815", color: "#60a5fa", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Run Stock Scan
                </button>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* ── Buy Modal ── */}
      {buyModal && (
        <BuyModal pick={buyModal} category={category} onClose={() => setBuyModal(null)} onBuy={loadData} />
      )}
    </main>
  );
}
